import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { comprasAPI, pagosAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ShoppingCart, Trash2, FileText, Package, Loader2, CreditCard, CheckCircle } from 'lucide-react';
import MercadoPagoCheckout from '../../components/MercadoPagoCheckout';

const TallerCarrito = () => {
  const { user } = useAuth();
  const [carrito, setCarrito] = useState([]);
  const [loading, setLoading] = useState(true);
  const [procesando, setProcesando] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);

  useEffect(() => {
    loadCarrito();
  }, []);

  const loadCarrito = async () => {
    try {
      const response = await comprasAPI.getCarrito();
      setCarrito(response.data?.items || []);
    } catch (error) {
      toast.error('Error al cargar el carrito');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveItem = async (item) => {
    try {
      await comprasAPI.quitarDelCarrito(item.tipo, item.documento_id, item.modelo_id);
      toast.success('Elemento eliminado');
      loadCarrito();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleProcesarCompra = () => {
    if (carrito.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }
    setShowCheckout(true);
  };

  const calcularTotal = () => {
    return carrito.reduce((acc, item) => acc + (item.precio_usd || 0), 0);
  };

  const getItemIcon = (tipo) => {
    switch (tipo) {
      case 'documento':
        return <FileText className="h-5 w-5 text-blue-600" />;
      case 'modelo':
        return <Package className="h-5 w-5 text-green-600" />;
      default:
        return <ShoppingCart className="h-5 w-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingCart className="h-6 w-6" /> Mi Carrito
          </h1>
          {carrito.length > 0 && (
            <Badge variant="secondary">{carrito.length} item(s)</Badge>
          )}
        </div>

        {carrito.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <ShoppingCart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-gray-600 mb-2">Tu carrito está vacío</p>
              <p className="text-gray-500">Explora la documentación y agrega los documentos que necesites.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Items del carrito */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Documentos seleccionados</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {carrito.map((item, idx) => (
                    <div key={item.id || idx} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="bg-gray-100 p-2 rounded-lg">
                          {getItemIcon(item.tipo)}
                        </div>
                        <div>
                          <p className="font-medium">{item.nombre}</p>
                          <Badge variant="outline" className="text-xs mt-1">{item.tipo}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-bold text-green-600">USD ${item.precio_usd?.toFixed(2) || '0.00'}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleRemoveItem(item)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Resumen */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Resumen de compra</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal ({carrito.length} items)</span>
                    <span>USD ${calcularTotal().toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between text-xl font-bold">
                      <span>Total</span>
                      <span className="text-green-600">USD ${calcularTotal().toFixed(2)}</span>
                    </div>
                  </div>
                  
                  {!showCheckout ? (
                    <>
                      {/* Aviso legal sobre servicio de visualización */}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                        <p className="text-xs text-amber-800 leading-relaxed">
                          <strong>Aviso importante:</strong> Usted está contratando el servicio de visualización 
                          en línea de la documentación técnica seleccionada a través de esta plataforma. 
                          Este pago no constituye la compra del documento en sí.
                        </p>
                      </div>
                      
                      <Button 
                        className="w-full mt-4 bg-teal-600 hover:bg-teal-700" 
                        size="lg"
                        onClick={handleProcesarCompra}
                        disabled={procesando}
                      >
                        {procesando ? (
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        ) : (
                          <CreditCard className="h-5 w-5 mr-2" />
                        )}
                        Contratar Servicio de Visualización
                      </Button>
                    </>
                  ) : (
                    <div className="mt-4 space-y-4">
                      <MercadoPagoCheckout
                        tipo="documentos"
                        titulo="Compra de Documentos"
                        descripcion={`${carrito.length} documento(s) técnico(s)`}
                        monto={calcularTotal()}
                        moneda="USD"
                        createPreference={async () => {
                          const documentos = carrito.map(item => ({
                            id: item.documento_id || item.id,
                            titulo: item.nombre,
                            codigo: item.codigo || '',
                            precio: item.precio_usd || 0
                          }));
                          const response = await pagosAPI.crearPreferenciaDocumentos({
                            documentos,
                            email: user?.email,
                            taller_id: user?.taller_id
                          });
                          return response.data;
                        }}
                        buttonText="Pagar Documentos"
                        onError={(error) => toast.error(error)}
                      />
                      <Button 
                        variant="outline"
                        onClick={() => setShowCheckout(false)}
                        className="w-full"
                      >
                        ← Volver al carrito
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default TallerCarrito;
